/**
 * Admin → Users.
 *
 * Single list grouped by organisation. Each row carries the user's
 * role as an inline editable select; renames open in a small inline
 * input. Any change PATCHes the user. New-user is a modal CTA in the
 * header.
 */

import { useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';

import type { AdminUserRow } from '@beacon/api-client';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useAdminUsers, useUpdateUser } from '@/hooks/use-users.js';

import { NewUserDialog } from './NewUserDialog.js';

const STAFF_ROLES = ['ops', 'staff'] as const;
const CLIENT_ROLES = ['client_admin', 'client_submitter', 'client_viewer'] as const;

export function AdminUsersList() {
  const usersQuery = useAdminUsers();
  const updateUser = useUpdateUser();
  const [newOpen, setNewOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  if (usersQuery.isLoading) {
    return <AdminUsersListSkeleton />;
  }
  if (usersQuery.isError) {
    return <div className="p-8 text-ink-300">Couldn't load users.</div>;
  }
  const users = usersQuery.data ?? [];

  // Group by org for display.
  const groups = new Map<string, AdminUserRow[]>();
  for (const u of users) {
    const arr = groups.get(u.organizationId) ?? [];
    arr.push(u);
    groups.set(u.organizationId, arr);
  }
  const orgs = [...groups.entries()]
    .map(([orgId, list]) => ({
      orgId,
      orgName: list[0]!.organizationName,
      orgType: list[0]!.organizationType,
      users: list.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      if (a.orgType !== b.orgType) return a.orgType === 'internal' ? -1 : 1;
      return a.orgName.localeCompare(b.orgName);
    });

  function commitNameEdit(u: AdminUserRow) {
    const next = draftName.trim();
    if (!next || next === u.name) {
      setEditingId(null);
      return;
    }
    updateUser.mutate(
      { id: u.id, input: { name: next } },
      {
        onSuccess: () => {
          toast.success('User renamed');
          setEditingId(null);
        },
      }
    );
  }

  function changeRole(u: AdminUserRow, role: AdminUserRow['role']) {
    if (role === u.role) return;
    updateUser.mutate(
      { id: u.id, input: { role } },
      {
        onSuccess: () => toast.success(`${u.name} is now ${role}`),
      }
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[12px] mono text-ink-300 uppercase tracking-[0.06em]">
              Admin
            </div>
            <h1 className="text-[19px] font-semibold text-ink-100 mt-0.5">
              Users
            </h1>
          </div>
          <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
            <Plus size={13} />
            New user
          </Button>
        </div>
      </header>

      <div className="p-6 space-y-4">
        {orgs.length === 0 ? (
          <div className="rounded-md border border-ink-600 bg-ink-850 p-8 text-center text-ink-300">
            No users yet.
          </div>
        ) : (
          orgs.map((g) => (
            <section
              key={g.orgId}
              className="rounded-md border border-ink-600 bg-ink-850 overflow-hidden"
            >
              <header className="px-4 py-3 border-b border-ink-600 flex items-center gap-2">
                <span className="text-[14px] font-medium text-ink-100">
                  {g.orgName}
                </span>
                <Badge tone={g.orgType === 'internal' ? 'slate' : 'blue'}>
                  {g.orgType.toUpperCase()}
                </Badge>
                <span className="ml-auto text-[11.5px] text-ink-400 mono">
                  {g.users.length} user{g.users.length === 1 ? '' : 's'}
                </span>
              </header>
              <ul className="divide-y divide-ink-700">
                {g.users.map((u) => {
                  const allowedRoles =
                    g.orgType === 'internal' ? STAFF_ROLES : CLIENT_ROLES;
                  const isEditing = editingId === u.id;
                  return (
                    <li
                      key={u.id}
                      className="px-4 py-2.5 flex items-center gap-3 text-[13px]"
                    >
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="text"
                              value={draftName}
                              autoFocus
                              onChange={(e) => setDraftName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitNameEdit(u);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              onBlur={() => commitNameEdit(u)}
                            />
                          </div>
                        ) : (
                          <button
                            className="group inline-flex items-center gap-1.5 text-ink-100 font-medium hover:text-beacon-300 focus-ring rounded"
                            onClick={() => {
                              setEditingId(u.id);
                              setDraftName(u.name);
                            }}
                          >
                            {u.name}
                            <Pencil
                              size={11}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-ink-300"
                            />
                          </button>
                        )}
                        <div className="text-[12px] text-ink-300 mono truncate">
                          {u.email}
                        </div>
                      </div>
                      <select
                        value={u.role}
                        onChange={(e) =>
                          changeRole(u, e.target.value as AdminUserRow['role'])
                        }
                        disabled={updateUser.isPending}
                        className="h-7 px-2 text-[12px] bg-ink-800 border border-ink-600 rounded-md focus-ring"
                      >
                        {allowedRoles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>

      <NewUserDialog open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
}

function AdminUsersListSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850">
        <Skeleton className="h-2.5 w-12 mb-1" />
        <Skeleton className="h-5 w-20" />
      </header>
      <div className="p-6 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <section
            key={i}
            className="rounded-md border border-ink-600 bg-ink-850 overflow-hidden"
          >
            <header className="px-4 py-3 border-b border-ink-600 flex items-center gap-2">
              <Skeleton className="h-3.5 w-32" inline />
              <Skeleton className="h-5 w-16" inline />
              <Skeleton className="h-3 w-12 ml-auto" inline />
            </header>
            <ul className="divide-y divide-ink-700">
              {Array.from({ length: 3 }).map((__, j) => (
                <li
                  key={j}
                  className="px-4 py-2.5 flex items-center gap-3"
                >
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-2.5 w-44" />
                  </div>
                  <Skeleton className="h-7 w-20" inline />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
